import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import navService from '../../services/navService';
import fxService from '../../services/fxService';
import CurrencySelect from './CurrencySelect';

// Teal color
const TEAL_COLOR = 'var(--teal)';

const PositionCalculator = ({ open, onClose }) => {
  // Input state
  const [accountSize, setAccountSize] = useState('');
  const [riskPercent, setRiskPercent] = useState('1');
  const [instrumentCurrency, setInstrumentCurrency] = useState('');
  const [entryPrice, setEntryPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [targetPrice, setTargetPrice] = useState('');

  // System state
  const [reportCurrency, setReportCurrency] = useState('');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingNav, setLoadingNav] = useState(false);
  const [loadingRate, setLoadingRate] = useState(false);
  const [error, setError] = useState(null);
  const [fxError, setFxError] = useState(null);
  const [manualRateMode, setManualRateMode] = useState(false);
  const [manualRate, setManualRate] = useState('');

  // Load latest NAV on open
  useEffect(() => {
    if (open) {
      loadLatestNav();
    }
  }, [open]);

  const loadLatestNav = async (currency = null) => {
    try {
      setLoadingNav(true);
      setError(null);

      // Get reporting currency first if not provided
      let reportCurr = currency;
      if (!reportCurr) {
        try {
          const currencyResult = await fxService.getReportingCurrency();
          // API returns { data: { reportingCurrency } } — the old code read `.currency`
          // (always undefined) so the calculator always fell back to USD. Use the real
          // field so it defaults to the user's actual reporting currency.
          reportCurr = currencyResult.data?.reportingCurrency || 'USD';
        } catch (currErr) {
          console.warn('Could not fetch reporting currency, using USD:', currErr);
          reportCurr = 'USD';
        }
        setReportCurrency(reportCurr);
        setInstrumentCurrency(reportCurr);
      }

      // Get current NAV in the specified currency
      try {
        const navResult = await navService.getCurrent({ reportingCurrency: reportCurr });
        // Backend returns currentNavReporting when reportingCurrency is specified, or currentNav otherwise
        const navValue = navResult.data?.currentNavReporting || navResult.data?.currentNav;
        if (navValue) {
          setAccountSize(parseFloat(navValue).toFixed(2));
        } else {
          // Try to get latest snapshot if current NAV not available
          const snapshotsResult = await navService.getSnapshots({ limit: 1, order: 'desc' });
          // Snapshots are returned as array directly in data
          const latestSnapshot = snapshotsResult.data?.[0];
          if (latestSnapshot?.total_nav) {
            setAccountSize(parseFloat(latestSnapshot.total_nav).toFixed(2));
          }
        }
      } catch (navErr) {
        console.warn('Could not fetch NAV:', navErr);
        // Don't show error - user can manually enter account size
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Could not load account data');
    } finally {
      setLoadingNav(false);
    }
  };

  const fetchExchangeRate = async () => {
    try {
      setLoadingRate(true);
      setFxError(null);
      setManualRateMode(false);

      const today = new Date().toISOString().split('T')[0];

      // Method 1: Try direct lookup for the specific pair (report -> instrument)
      try {
        const result = await fxService.lookupRate(reportCurrency, instrumentCurrency, today);
        if (result.data?.rate) {
          setExchangeRate(result.data.rate);
          return;
        }
      } catch (lookupErr) {
        console.warn('Direct lookup failed:', lookupErr);
      }

      // Method 2: Try reverse lookup (instrument -> report) and invert
      try {
        const reverseResult = await fxService.lookupRate(instrumentCurrency, reportCurrency, today);
        if (reverseResult.data?.rate) {
          setExchangeRate(1 / reverseResult.data.rate);
          return;
        }
      } catch (reverseErr) {
        console.warn('Reverse lookup failed:', reverseErr);
      }

      // Method 3: Try getCurrentRates and calculate cross rate via AUD (our base currency)
      try {
        const currentRates = await fxService.getCurrentRates();
        if (currentRates.data?.rates && Array.isArray(currentRates.data.rates)) {
          const ratesArray = currentRates.data.rates;

          // Backend stores rates as FROM->TO pairs (AUD-based)
          const findRate = (from, to) => {
            const rate = ratesArray.find(r =>
              r.fromCurrency === from && r.toCurrency === to
            );
            return rate?.rate || null;
          };

          // Try to find direct rate first
          let directRate = findRate(reportCurrency, instrumentCurrency);
          if (directRate) {
            setExchangeRate(directRate);
            return;
          }

          // Try reverse
          let reverseRate = findRate(instrumentCurrency, reportCurrency);
          if (reverseRate) {
            setExchangeRate(1 / reverseRate);
            return;
          }

          // Try cross-rate via AUD (our base currency in the database)
          // To get reportCurrency -> instrumentCurrency:
          // 1. reportCurrency -> AUD (use AUD->report rate inverted)
          // 2. AUD -> instrumentCurrency
          const audToInstrument = findRate('AUD', instrumentCurrency);
          const audToReport = findRate('AUD', reportCurrency);

          if (audToInstrument && audToReport) {
            // reportCurrency -> AUD -> instrumentCurrency
            // = (1/audToReport) * audToInstrument = audToInstrument / audToReport
            setExchangeRate(audToInstrument / audToReport);
            return;
          }

          // Try with inverted AUD rates
          const instrumentToAud = findRate(instrumentCurrency, 'AUD');
          const reportToAud = findRate(reportCurrency, 'AUD');

          if (instrumentToAud && reportToAud) {
            // reportCurrency -> AUD -> instrumentCurrency
            // = reportToAud * (1/instrumentToAud) = reportToAud / instrumentToAud
            setExchangeRate(reportToAud / instrumentToAud);
            return;
          }

          // Mixed: one direction available for each
          if (audToInstrument && reportToAud) {
            // reportCurrency -> AUD (reportToAud) -> instrumentCurrency (audToInstrument)
            setExchangeRate(reportToAud * audToInstrument);
            return;
          }

          if (instrumentToAud && audToReport) {
            // reportCurrency -> AUD (1/audToReport) -> instrumentCurrency (1/instrumentToAud)
            setExchangeRate(1 / (audToReport * instrumentToAud));
            return;
          }
        }
      } catch (currentErr) {
        console.warn('Current rates lookup failed:', currentErr);
      }

      // All methods failed
      throw new Error('Rate not available');
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
      setFxError('Enter rate manually');
      setManualRateMode(true);
      setExchangeRate(null);
    } finally {
      setLoadingRate(false);
    }
  };

  // Fetch exchange rate when instrument currency changes
  useEffect(() => {
    if (instrumentCurrency && instrumentCurrency !== reportCurrency) {
      fetchExchangeRate();
    } else {
      setExchangeRate(null);
      setFxError(null);
      setManualRateMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentCurrency, reportCurrency]);

  // Calculate outputs
  const calculateOutputs = useCallback(() => {
    const account = parseFloat(accountSize) || 0;
    const risk = parseFloat(riskPercent) || 0;
    const entry = parseFloat(entryPrice) || 0;
    const stop = parseFloat(stopLoss) || 0;
    const target = parseFloat(targetPrice) || 0;

    // Check for required inputs
    if (!account || !risk || !entry || !stop) {
      return { positionSize: null, dollarRisk: null, riskPerUnit: null, rewardRisk: null };
    }

    // Calculate risk amount in report currency
    const riskAmountReport = account * (risk / 100);

    // Get effective exchange rate
    let effectiveRate = 1;
    if (instrumentCurrency !== reportCurrency) {
      if (manualRateMode) {
        effectiveRate = parseFloat(manualRate) || 0;
        if (!effectiveRate) {
          return { positionSize: null, dollarRisk: riskAmountReport, riskPerUnit: null, rewardRisk: null };
        }
      } else {
        effectiveRate = exchangeRate || 0;
        if (!effectiveRate) {
          return { positionSize: null, dollarRisk: riskAmountReport, riskPerUnit: null, rewardRisk: null };
        }
      }
    }

    // Convert risk to instrument currency
    const riskAmountInstrument = riskAmountReport * effectiveRate;

    // Calculate risk per unit (absolute value for short positions)
    const riskPerUnit = Math.abs(entry - stop);

    // Prevent division by zero
    if (riskPerUnit === 0) {
      return { positionSize: null, dollarRisk: riskAmountReport, riskPerUnit: 0, rewardRisk: null };
    }

    // Calculate position size
    const positionSize = riskAmountInstrument / riskPerUnit;

    // Calculate reward:risk if target provided
    let rewardRisk = null;
    if (target) {
      const rewardPerUnit = Math.abs(target - entry);
      rewardRisk = riskPerUnit > 0 ? (rewardPerUnit / riskPerUnit) : null;
    }

    return {
      positionSize,
      dollarRisk: riskAmountReport,
      riskPerUnit,
      rewardRisk
    };
  }, [accountSize, riskPercent, entryPrice, stopLoss, targetPrice, instrumentCurrency, reportCurrency, exchangeRate, manualRateMode, manualRate]);

  const outputs = calculateOutputs();

  const handleReset = () => {
    setRiskPercent('1');
    setInstrumentCurrency(reportCurrency);
    setEntryPrice('');
    setStopLoss('');
    setTargetPrice('');
    setManualRate('');
    setManualRateMode(false);
    setFxError(null);
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return num.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatPositionSize = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    // Round to whole number for most instruments
    return Math.floor(num).toLocaleString('en-AU');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateIcon sx={{ color: TEAL_COLOR }} />
          <Typography variant="h6">Position Calculator</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 3 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Inputs */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {/* Account Size */}
          <TextField
            label={`Account Size${reportCurrency ? ` (${reportCurrency})` : ''}`}
            value={accountSize}
            onChange={(e) => setAccountSize(e.target.value)}
            type="number"
            fullWidth
            placeholder={loadingNav ? 'Loading...' : ''}
            InputProps={{
              endAdornment: loadingNav ? (
                <CircularProgress size={20} />
              ) : reportCurrency ? (
                <InputAdornment position="end">{reportCurrency}</InputAdornment>
              ) : null
            }}
          />

          {/* Risk Percent */}
          <TextField
            label="Risk %"
            value={riskPercent}
            onChange={(e) => setRiskPercent(e.target.value)}
            type="number"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            inputProps={{ step: 0.1, min: 0.1, max: 100 }}
          />

          {/* Instrument Currency */}
          <CurrencySelect
            value={instrumentCurrency}
            onChange={(value) => setInstrumentCurrency(value)}
            label="Instrument Currency"
            fullWidth
          />

          {/* Exchange Rate Display */}
          {instrumentCurrency && instrumentCurrency !== reportCurrency && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loadingRate ? (
                <CircularProgress size={16} />
              ) : manualRateMode ? (
                <TextField
                  label={`Manual Rate (1 ${reportCurrency} = ? ${instrumentCurrency})`}
                  value={manualRate}
                  onChange={(e) => setManualRate(e.target.value)}
                  type="number"
                  size="small"
                  fullWidth
                  error={!!fxError}
                  helperText={fxError}
                  inputProps={{ step: 0.0001 }}
                />
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Rate: 1 {reportCurrency} = {formatNumber(exchangeRate, 4)} {instrumentCurrency}
                  </Typography>
                  <IconButton size="small" onClick={fetchExchangeRate} title="Refresh rate">
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          )}

          {/* Entry Price */}
          <TextField
            label="Entry Price"
            value={entryPrice}
            onChange={(e) => setEntryPrice(e.target.value)}
            type="number"
            fullWidth
            InputProps={{
              startAdornment: instrumentCurrency && <InputAdornment position="start">{instrumentCurrency}</InputAdornment>
            }}
            inputProps={{ step: 0.01 }}
          />

          {/* Stop Loss */}
          <TextField
            label="Stop Loss"
            value={stopLoss}
            onChange={(e) => setStopLoss(e.target.value)}
            type="number"
            fullWidth
            InputProps={{
              startAdornment: instrumentCurrency && <InputAdornment position="start">{instrumentCurrency}</InputAdornment>
            }}
            inputProps={{ step: 0.01 }}
          />

          {/* Target Price (Optional) */}
          <TextField
            label="Target Price (Optional)"
            value={targetPrice}
            onChange={(e) => setTargetPrice(e.target.value)}
            type="number"
            fullWidth
            InputProps={{
              startAdornment: instrumentCurrency && <InputAdornment position="start">{instrumentCurrency}</InputAdornment>
            }}
            inputProps={{ step: 0.01 }}
          />
        </Box>

        {/* Outputs */}
        <Paper sx={{ p: 2, bgcolor: 'var(--bg-table-header)', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Results
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {/* Position Size - Prominent */}
            <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Position Size
              </Typography>
              <Typography variant="h4" sx={{ color: TEAL_COLOR, fontWeight: 600 }}>
                {formatPositionSize(outputs.positionSize)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                units
              </Typography>
            </Box>

            {/* Dollar Risk */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Dollar Risk
              </Typography>
              <Typography variant="h6">
                {outputs.dollarRisk !== null ? `${reportCurrency} ${formatNumber(outputs.dollarRisk)}` : '-'}
              </Typography>
            </Box>

            {/* Risk per Unit */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                Risk per Unit
              </Typography>
              <Typography variant="h6">
                {outputs.riskPerUnit !== null ? formatNumber(outputs.riskPerUnit) : '-'}
              </Typography>
            </Box>

            {/* Reward:Risk Ratio */}
            {targetPrice && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="body2" color="text.secondary">
                  Reward:Risk Ratio
                </Typography>
                <Typography variant="h6" sx={{ color: outputs.rewardRisk >= 2 ? 'success.main' : 'inherit' }}>
                  {outputs.rewardRisk !== null ? `${formatNumber(outputs.rewardRisk, 1)}:1` : '-'}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Tip: Press Ctrl+K to open anytime
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            <Button variant="contained" onClick={onClose} sx={{ bgcolor: 'var(--teal)', '&:hover': { bgcolor: 'var(--teal-hover)' } }}>
              Close
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PositionCalculator;
